import { getSubunitMultiplier } from '@bbpos/utils';

/**
 * 3-way match (PO ↔ goods receipt ↔ vendor bill) per PO line — pure so the
 * detail page's panel is unit-tested. All money is ex-tax minor units of the tenant currency.
 *
 * A line is MATCHED when received qty = ordered qty = billed qty and the
 * billed unit price is within priceTolerance(currency) of the PO net unit price.
 * Anything else is surfaced as a variance; nothing here blocks payment.
 */
export type MatchStatus =
  | 'MATCHED'
  | 'QTY_VARIANCE'
  | 'PRICE_VARIANCE'
  | 'QTY_AND_PRICE_VARIANCE'
  | 'AWAITING_RECEIPT'
  | 'AWAITING_BILL';

/**
 * One major unit per unit (₹1 = 100 paise, 1 KWD = 1000 fils) — absorbs minor-unit
 * rounding between PO, GRN and bill lines in the tenant's currency.
 */
export function priceTolerance(currency: string): number {
  return getSubunitMultiplier(currency);
}

export interface MatchInput {
  quantity: number;
  received_quantity: number;
  billed_quantity?: number;
  ordered_value?: number;
  received_value?: number;
  billed_value?: number;
}

export interface MatchResult {
  status: MatchStatus;
  /** received − ordered (positive = over-received) */
  qty_delta: number;
  /** billed − received */
  billed_qty_delta: number;
  /** PO net unit price (minor units), null when ordered qty is 0 */
  po_unit: number | null;
  /** Billed unit price (minor units), null when nothing billed */
  billed_unit: number | null;
  /** billed_unit − po_unit, null until both exist */
  price_delta: number | null;
}

export function matchLine(item: MatchInput, currency = 'INR'): MatchResult {
  const ordered = item.quantity;
  const received = item.received_quantity;
  const billed = item.billed_quantity ?? 0;
  const po_unit = ordered > 0 ? Math.floor((item.ordered_value ?? 0) / ordered) : null;
  const billed_unit = billed > 0 ? Math.floor((item.billed_value ?? 0) / billed) : null;
  const price_delta = po_unit != null && billed_unit != null ? billed_unit - po_unit : null;

  const qty_delta = received - ordered;
  const billed_qty_delta = billed - received;

  let status: MatchStatus;
  if (received <= 0) status = 'AWAITING_RECEIPT';
  else if (billed <= 0) status = 'AWAITING_BILL';
  else {
    const qtyOff = qty_delta !== 0 || billed_qty_delta !== 0;
    const priceOff = price_delta != null && Math.abs(price_delta) > priceTolerance(currency);
    status = qtyOff && priceOff ? 'QTY_AND_PRICE_VARIANCE' : qtyOff ? 'QTY_VARIANCE' : priceOff ? 'PRICE_VARIANCE' : 'MATCHED';
  }
  return { status, qty_delta, billed_qty_delta, po_unit, billed_unit, price_delta };
}

export interface MatchTotals {
  ordered_value: number;
  received_value: number;
  billed_value: number;
  matched_lines: number;
  variance_lines: number;
  pending_lines: number;
}

export function matchTotals(items: MatchInput[], currency = 'INR'): MatchTotals {
  const t: MatchTotals = { ordered_value: 0, received_value: 0, billed_value: 0, matched_lines: 0, variance_lines: 0, pending_lines: 0 };
  for (const i of items) {
    t.ordered_value += i.ordered_value ?? 0;
    t.received_value += i.received_value ?? 0;
    t.billed_value += i.billed_value ?? 0;
    const s = matchLine(i, currency).status;
    if (s === 'MATCHED') t.matched_lines += 1;
    else if (s === 'AWAITING_RECEIPT' || s === 'AWAITING_BILL') t.pending_lines += 1;
    else t.variance_lines += 1;
  }
  return t;
}

export const MATCH_LABELS: Record<MatchStatus, string> = {
  MATCHED: 'Matched',
  QTY_VARIANCE: 'Qty variance',
  PRICE_VARIANCE: 'Price variance',
  QTY_AND_PRICE_VARIANCE: 'Qty & price variance',
  AWAITING_RECEIPT: 'Awaiting receipt',
  AWAITING_BILL: 'Awaiting bill',
};
